import {useActivationTraceUrl} from '@github-ui/agent-sessions/hooks/use-activation-trace-url'
import {useConnectionTraceUrl} from '@github-ui/agent-sessions/hooks/use-connection-trace-url'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {DebugLogButton as SharedDebugLogButton} from '@github-ui/copilot-shared-ui/components/DebugLogButton'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {isStaff} from '@github-ui/stats'
import {useEffect} from 'react'

import {useAppendDebugEvent, useDebugEvents} from '../providers/use-timeline-contexts'

function useAutoHostTelemetryCapture(): void {
  const appendDebugEvent = useAppendDebugEvent()
  const session = useOptionalEstablishedAhpSession()
  const {isBrowser} = useRuntimeEnvironment()
  const subscribeTelemetry = session?.view.subscribeTelemetry
  const enabled = isBrowser && subscribeTelemetry !== undefined && isStaff()

  useEffect(() => {
    if (!enabled || !subscribeTelemetry) return
    let cancelled = false
    let unsubscribe: (() => void) | undefined
    void (async () => {
      try {
        const result = await subscribeTelemetry({
          onEvent: event => {
            appendDebugEvent({
              id: crypto.randomUUID(),
              timestamp: new Date().toISOString(),
              source: 'relay',
              direction: 'inbound',
              message: JSON.stringify({
                method:
                  event.signal === 'logs'
                    ? 'otlp/exportLogs'
                    : event.signal === 'traces'
                      ? 'otlp/exportTraces'
                      : 'otlp/exportMetrics',
                channel: event.channel,
                payload: event.payload,
              }),
            })
          },
          onError: message => {
            appendDebugEvent({
              id: crypto.randomUUID(),
              timestamp: new Date().toISOString(),
              source: 'relay',
              direction: 'status',
              message: `OTLP telemetry subscription failed: ${message}`,
            })
          },
        })
        if (cancelled) {
          result()
          return
        }
        unsubscribe = result
      } catch (error) {
        if (cancelled) return
        appendDebugEvent({
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
          source: 'relay',
          direction: 'status',
          message: `OTLP telemetry subscription failed: ${
            error instanceof Error ? error.message : 'Unknown telemetry subscription error'
          }`,
        })
      }
    })()
    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [enabled, subscribeTelemetry, appendDebugEvent])
}

export function DebugLogButton({defaultOpen = false, taskId}: {defaultOpen?: boolean; taskId?: string}) {
  const debugEvents = useDebugEvents()
  const session = useOptionalEstablishedAhpSession()
  const activationTraceUrl = useActivationTraceUrl(taskId ?? session?.identity.taskId, session?.identity.sessionId)
  const connectionTraceUrl = useConnectionTraceUrl(taskId ?? session?.identity.taskId, session?.identity.sessionId)

  useAutoHostTelemetryCapture()

  return (
    <SharedDebugLogButton
      debugEvents={debugEvents}
      defaultOpen={defaultOpen}
      activationTraceUrl={activationTraceUrl}
      connectionTraceUrl={connectionTraceUrl}
    />
  )
}
